import type { DeviceResponseV5 } from '@emma/api_client/types';
import {
  Box,
  Button,
  DatePickerNative,
  Dropdown,
  Flex,
  FormControl,
  Icon,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Tag,
  Text,
} from '@lego/klik-ui';
import { TrashSolid, UserSolid } from '@lego/klik-ui/icons';
import { useCallback, useState } from 'react';
import type { RolloutWindowType } from '@/hooks/rollout_windows';
import { DevicePickerModal } from './DevicePickerModal';
import type { RolloutWave, WaveTargetingMode } from './types';

type Props = {
  wave: RolloutWave;
  waveNumber: number;
  availableDevices: DeviceResponseV5[];
  isDisabled?: boolean;
  isLoadingDevices: boolean;
  onChange: (updated: RolloutWave) => void;
  onRemove: () => void;
  canRemove: boolean;
  maxPercentage?: number;
};

export const WaveEditor = ({
  wave,
  waveNumber,
  availableDevices,
  isDisabled = false,
  isLoadingDevices,
  onChange,
  onRemove,
  canRemove,
  maxPercentage = 100,
}: Props) => {
  const [isDevicePickerOpen, setIsDevicePickerOpen] = useState(false);

  const update = useCallback(
    (partial: Partial<RolloutWave>) => {
      if (!isDisabled) onChange({ ...wave, ...partial });
    },
    [isDisabled, wave, onChange]
  );

  const handleStartTimeChange = (value: string) => {
    update({ schedule: { kind: 'oneTime', startTime: value } });
  };

  const handleTargetingModeChange = (mode: WaveTargetingMode) => {
    update({ targetingMode: mode });
  };

  const handleDevicesConfirmed = (ids: number[]) => {
    update({ deviceIds: ids });
  };

  const selectedDevices = availableDevices.filter((d) => wave.deviceIds.includes(d.id));

  return (
    <Box
      borderColor="gray.200"
      borderRadius="md"
      borderWidth="1px"
      opacity={isDisabled ? 0.6 : 1}
      p={4}
    >
      <Flex align="center" justify="space-between" mb={4}>
        <Flex align="center" gap={2}>
          <Tag colorScheme="light-blue" size="sm">
            Wave {waveNumber}
          </Tag>
          {wave.targetingMode === 'percentage' && (
            <Tag colorScheme="light-blue" size="sm">
              {Math.min(wave.percentage, maxPercentage)}% of fleet
            </Tag>
          )}
          {wave.targetingMode === 'specific' && (
            <Tag colorScheme="teal" size="sm">
              {wave.deviceIds.length} device{wave.deviceIds.length !== 1 ? 's' : ''} selected
            </Tag>
          )}
        </Flex>
        {canRemove && (
          <IconButton
            aria-label="Remove wave"
            icon={<TrashSolid />}
            isDisabled={isDisabled}
            onClick={onRemove}
            size="sm"
            variant="ghost"
          />
        )}
      </Flex>

      <Stack direction="column" spacing={4}>
        <FormControl isRequired>
          <FormControl.Label>Start Time</FormControl.Label>
          <DatePickerNative
            isDisabled={isDisabled}
            label=""
            onChange={handleStartTimeChange}
            type="datetime-local"
            value={wave.schedule.startTime}
            width="100%"
          />
        </FormControl>

        <FormControl isRequired>
          <FormControl.Label>Duration (seconds)</FormControl.Label>
          <Input
            isDisabled={isDisabled}
            onChange={({ target: { value } }) =>
              update({ durationInSeconds: value ? Number(value) : undefined })
            }
            placeholder="e.g. 3600"
            type="number"
            value={wave.durationInSeconds ?? ''}
          />
          <FormControl.HelperText>How long the rollout window stays open</FormControl.HelperText>
        </FormControl>

        <FormControl>
          <FormControl.Label>Allowed Update Type</FormControl.Label>
          <Dropdown
            isDisabled={isDisabled}
            onChange={(value: string) => update({ type: value as RolloutWindowType })}
            value={[wave.type]}
            width="100%"
          >
            <Dropdown.Option value="APPLICATION">Application only</Dropdown.Option>
            <Dropdown.Option value="APPLICATION_AND_OS">Application and OS</Dropdown.Option>
          </Dropdown>
        </FormControl>

        <FormControl>
          <FormControl.Label>Device Targeting</FormControl.Label>
          <RadioGroup
            onChange={(val: string) => handleTargetingModeChange(val as WaveTargetingMode)}
            value={wave.targetingMode}
          >
            <Flex gap={4}>
              <Radio isDisabled={isDisabled} value="percentage">
                Percentage of fleet
              </Radio>
              <Radio isDisabled={isDisabled} value="specific">
                Specific devices
              </Radio>
            </Flex>
          </RadioGroup>
        </FormControl>

        {wave.targetingMode === 'percentage' ? (
          <FormControl>
            <FormControl.Label>Percentage of fleet</FormControl.Label>
            <Input
              isDisabled={isDisabled}
              max={maxPercentage}
              min={1}
              onBlur={() => {
                const clamped = Math.min(Math.max(1, wave.percentage), maxPercentage);
                if (clamped !== wave.percentage) {
                  update({ percentage: clamped });
                }
              }}
              onChange={({ target: { value } }) => {
                const num = Number(value);
                if (value === '' || Number.isNaN(num)) return;
                update({ percentage: num });
              }}
              placeholder="e.g. 50"
              type="number"
              value={wave.percentage}
            />
            <FormControl.HelperText>
              Devices are assigned sequentially from the fleet list.
              {maxPercentage < 100 && ` Max available: ${maxPercentage}% (remaining fleet budget).`}
            </FormControl.HelperText>
          </FormControl>
        ) : (
          <FormControl>
            <FormControl.Label>Selected Devices</FormControl.Label>
            <Flex align="center" gap={2} wrap="wrap">
              {selectedDevices.length === 0 ? (
                <Text color="gray.500" fontSize="sm">
                  No devices selected
                </Text>
              ) : (
                selectedDevices.map((d) => (
                  <Tag colorScheme="teal" key={d.id} size="sm">
                    {d.name}
                  </Tag>
                ))
              )}
              <Button
                isDisabled={isDisabled}
                leftIcon={<Icon as={UserSolid} />}
                onClick={() => setIsDevicePickerOpen(true)}
                size="sm"
                variant="outline"
              >
                {wave.deviceIds.length === 0 ? 'Select devices' : 'Edit selection'}
              </Button>
            </Flex>
          </FormControl>
        )}
      </Stack>

      <DevicePickerModal
        devices={availableDevices}
        isLoading={isLoadingDevices}
        isOpen={isDevicePickerOpen}
        onClose={() => setIsDevicePickerOpen(false)}
        onConfirm={handleDevicesConfirmed}
        selectedDeviceIds={wave.deviceIds}
      />
    </Box>
  );
};
